'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, Shield, X } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import LanguageSelector from '@/components/language/LanguageSelector';
import { useTranslation } from '@/i18n/useTranslation';

const NAV_LINKS = [
  { label: 'Product', href: '/product', tKey: 'nav.product' },
  { label: 'Memory Lab', href: '/interactive', tKey: 'nav.memory_lab' },
  { label: 'Preservation', href: '/preservation-ledger', tKey: 'nav.preservation' },
  { label: 'Trust Ledger', href: '/trust-ledger', tKey: 'nav.trust_ledger' },
  { label: 'For Institutions', href: '/for-institutions', tKey: 'nav.for_institutions' },
  { label: 'Pricing', href: '/pricing', tKey: 'nav.pricing' },
  { label: 'Blog', href: '/blog', tKey: 'nav.blog' },
  { label: 'Verify', href: '/verify', tKey: 'nav.verify' },
  { label: 'About', href: '/about', tKey: 'nav.about' },
];

const MOBILE_LINKS = [
  ...NAV_LINKS,
  { label: 'Press', href: '/press', tKey: 'nav.press' },
  { label: 'Contact', href: '/contact', tKey: 'nav.contact' },
];

function isCurrentPath(pathname: string, href: string) {
  const path = href.split('?')[0];
  return pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));
}

// Keep the mobile menu's open/close transition short and predictable -- long or
// unbounded animations are what make a menu feel laggy or unresponsive on mobile.
const MENU_TRANSITION_MS = 180;

export default function LandingNav() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  // `mobileOpen` is the semantic open/closed state (drives aria + the enter/exit
  // transition). `menuMounted` keeps the panel in the DOM for the exit transition
  // after `mobileOpen` flips back to false, then unmounts it. `isClosing` blocks
  // the toggle button for the duration of that exit transition only, so a rapid
  // second tap can't fire a conflicting open while the panel is animating away.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuMounted, setMenuMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  function navLabel(item: { label: string; tKey: string }) {
    return t(item.tKey as any) || item.label;
  }

  function navLinkTitle(label: string) {
    return label === 'Memory Lab' ? t('nav.memory_lab_hint' as any) : undefined;
  }

  function openMenu() {
    if (isClosing) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMenuMounted(true);
    // Mount closed first, then flip to open on the next frame so the browser has
    // a starting style to transition from (otherwise it just appears instantly).
    requestAnimationFrame(() => setMobileOpen(true));
  }

  function closeMenu() {
    if (!menuMounted || isClosing) return;
    setMobileOpen(false);
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setMenuMounted(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, MENU_TRANSITION_MS);
  }

  function toggleMenu() {
    if (isClosing) return;
    if (mobileOpen) closeMenu();
    else openMenu();
  }

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 16);
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Route changes close the menu immediately -- the destination page is already
  // replacing this one, so there is nothing worth animating.
  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMobileOpen(false);
    setMenuMounted(false);
    setIsClosing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  // Body scroll stays locked for the full mounted lifetime (open + closing), not
  // just while `mobileOpen` is true, so the page doesn't jump/scroll mid-close.
  useEffect(() => {
    if (!menuMounted) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuMounted]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOpen]);

  return (
    <>
      <header
        className={`public-header fixed inset-x-0 top-0 z-50 px-4 transition-[box-shadow] duration-200 ease-out xl:hidden ${
          scrolled
            ? 'shadow-[0_8px_24px_rgba(15,23,42,0.10)]'
            : 'shadow-[0_4px_16px_rgba(15,118,86,0.08)]'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
          style={{ background: 'var(--public-family-accent)' }}
        />
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between">
          <Link
            href="/"
            aria-label={t('nav.home_aria' as any)}
            className="flex shrink-0 items-center gap-2 rounded-lg"
          >
            <AppLogo size={32} />
            <span className="text-lg font-bold tracking-tight text-slate-950">Ertzyx</span>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              href="/register"
              className="public-button public-button-primary hidden min-h-0 px-3 py-2 text-xs sm:inline-flex"
            >
              <Shield size={13} aria-hidden="true" />
              {t('nav.open_vault_short' as any)}
            </Link>
            <button
              type="button"
              onClick={toggleMenu}
              disabled={isClosing}
              aria-label={mobileOpen ? t('nav.close_menu' as any) : t('nav.open_menu' as any)}
              aria-expanded={mobileOpen}
              aria-controls="public-mobile-menu"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--public-border-strong)] bg-[var(--public-surface)] text-[var(--public-text)] shadow-sm transition-[background-color,border-color,transform] duration-150 ease-out hover:border-[var(--public-family-accent)] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {mobileOpen ? (
                <X size={20} aria-hidden="true" />
              ) : (
                <Menu size={20} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>

      {menuMounted && (
        <>
          <button
            type="button"
            aria-label={t('nav.close_menu' as any)}
            onClick={closeMenu}
            className={`fixed inset-0 z-30 bg-slate-950/25 xl:hidden transition-opacity ease-out ${
              mobileOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDuration: `${MENU_TRANSITION_MS}ms` }}
          />
          <div
            id="public-mobile-menu"
            className="fixed inset-x-0 z-40 px-3 pb-3 sm:px-4 xl:hidden"
            style={{ top: 'calc(64px + env(safe-area-inset-top))' }}
          >
            <div
              className={`public-header mx-auto max-w-2xl overflow-y-auto overscroll-contain rounded-2xl shadow-[0_24px_64px_rgba(15,23,42,0.18)] transition-[opacity,transform] ease-out ${
                mobileOpen ? 'translate-y-0 opacity-100' : '-translate-y-1.5 opacity-0'
              }`}
              style={{
                maxHeight: 'calc(100dvh - 76px - env(safe-area-inset-top))',
                paddingBottom: 'env(safe-area-inset-bottom)',
                transitionDuration: `${MENU_TRANSITION_MS}ms`,
              }}
            >
              <nav aria-label="Mobile navigation" className="p-3 sm:p-4">
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {MOBILE_LINKS.map((item) => {
                    const active = isCurrentPath(pathname, item.href);
                    return (
                      <Link
                        key={item.label}
                        href={item.href}
                        aria-current={active ? 'page' : undefined}
                        title={navLinkTitle(item.label)}
                        onClick={closeMenu}
                        className={`flex min-h-11 items-center rounded-xl px-3.5 py-2.5 text-sm transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.99] ${
                          active
                            ? 'public-nav-active font-black'
                            : 'font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className="mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{
                            background: active ? 'var(--public-family-accent)' : 'transparent',
                          }}
                        />
                        {navLabel(item)}
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-3 border-t border-slate-200 pt-3">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Link
                      href="/login"
                      onClick={closeMenu}
                      className="public-button public-button-secondary flex-1"
                    >
                      {t('nav.login' as any)}
                    </Link>
                    <Link
                      href="/register"
                      onClick={closeMenu}
                      className="public-button public-button-primary flex-1"
                    >
                      <Shield size={15} aria-hidden="true" />
                      {t('nav.open_vault' as any)}
                    </Link>
                  </div>
                  <div className="mt-3 flex justify-center">
                    <LanguageSelector variant="ghost" size="sm" placement="up" />
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </>
      )}

      <nav
        aria-label="Primary navigation"
        className="fixed inset-x-0 top-0 z-50 hidden px-4 pt-3 xl:block"
      >
        <div
          className={`public-header mx-auto max-w-screen-2xl rounded-2xl px-5 transition-[box-shadow] duration-200 ease-out 2xl:px-7 ${
            scrolled
              ? 'shadow-[0_14px_36px_rgba(15,23,42,0.14)]'
              : 'shadow-[0_8px_28px_rgba(15,118,86,0.12)]'
          }`}
        >
          <div className="flex h-[60px] items-center gap-4 2xl:gap-6">
            <Link
              href="/"
              aria-label={t('nav.home_aria' as any)}
              className="flex shrink-0 items-center gap-2 rounded-lg"
            >
              <AppLogo size={34} />
              <span className="text-xl font-bold tracking-tight text-slate-950">Ertzyx</span>
            </Link>

            <div className="flex min-w-0 flex-1 items-center justify-center gap-0.5 2xl:gap-1">
              {NAV_LINKS.map((item) => {
                const active = isCurrentPath(pathname, item.href);
                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    aria-current={active ? 'page' : undefined}
                    title={navLinkTitle(item.label)}
                    className={`whitespace-nowrap rounded-lg px-2 py-2 text-[11px] tracking-[-0.01em] transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.98] 2xl:px-2.5 2xl:text-xs ${
                      active
                        ? 'public-nav-active font-black'
                        : 'font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-950'
                    }`}
                  >
                    {navLabel(item)}
                  </Link>
                );
              })}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <LanguageSelector variant="ghost" size="sm" />
              <Link
                href="/login"
                className="public-button public-button-secondary min-h-0 px-3 py-2 text-xs 2xl:px-4 2xl:text-sm"
              >
                {t('nav.login' as any)}
              </Link>
              <Link
                href="/register"
                className="public-button public-button-primary min-h-0 whitespace-nowrap px-3 py-2 text-xs 2xl:px-4 2xl:text-sm"
              >
                <Shield size={14} className="shrink-0" aria-hidden="true" />
                <span className="2xl:hidden">{t('nav.open_vault_short' as any)}</span>
                <span className="hidden 2xl:inline">{t('nav.open_vault' as any)}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
